import React, { useState, useEffect, useCallback } from "react";
import { db } from "@/api/db";
import { Briefcase, RefreshCw, Check, X, Star, EyeOff, AlertCircle, Loader2 } from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const FLAG_EMOJI = { Brasil: "🇧🇷", Portugal: "🇵🇹", Global: "🌎" };

const SOURCE_STYLE = {
  Portus:       { background: "#B0FCEA", color: "#003326" },
  Remotive:     { background: "#D5F9D7", color: "#008572" },
  WWR:          { background: "#FEEDB4", color: "#65541B" },
  Gupy:         { background: "#E8F4FD", color: "#0066CC" },
  VagasRemotas: { background: "#FEE8E0", color: "#C0392B" },
  GetOnBoard:   { background: "#F0E8FD", color: "#6C3483" },
};

const AVATAR_COLORS = ["#00664C", "#008572", "#00DCA4", "#65541B", "#892E38"];

function avatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CompanyAvatar({ logo, name }) {
  const [imgError, setImgError] = useState(false);
  const initial = name?.[0]?.toUpperCase() ?? "?";

  if (logo && !imgError) {
    return (
      <img
        src={logo}
        alt={name}
        className="w-10 h-10 rounded-xl object-contain bg-white flex-shrink-0"
        style={{ border: "1px solid #E5E9EA" }}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
      style={{ background: avatarColor(name) }}
    >
      {initial}
    </div>
  );
}

function JobRow({ job, actions }) {
  const tags = Array.isArray(job.tags) ? job.tags : [];
  const sourceStyle = SOURCE_STYLE[job.source] ?? { background: "#E5E9EA", color: "#646C6F" };

  return (
    <div className="rounded-2xl p-4 bg-white flex gap-3" style={{ border: "1px solid #E5E9EA" }}>
      <CompanyAvatar logo={job.company_logo_url} name={job.company} />

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          {job.is_featured && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#00DCA4", color: "#003326" }}>
              ⭐
            </span>
          )}
          <span className="font-semibold text-sm" style={{ color: "#2E3233", fontFamily: "Poppins, sans-serif" }}>
            {job.title}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs" style={{ color: "#99A6AB" }}>
          <span className="font-medium" style={{ color: "#646C6F" }}>{job.company}</span>
          <span>{FLAG_EMOJI[job.country] ?? "🌍"} {job.country}</span>
          {job.source && (
            <span className="px-1.5 py-0.5 rounded-full font-semibold" style={sourceStyle}>{job.source}</span>
          )}
          {job.language && (
            <span className="px-1.5 py-0.5 rounded-full" style={{ background: "#EEF2FF", color: "#4338CA" }}>
              {job.language === "Inglês" ? "🇬🇧 Inglês" : "🇧🇷 Português"}
            </span>
          )}
          {tags.slice(0, 3).map(t => (
            <span key={t} className="px-1.5 py-0.5 rounded-full" style={{ background: "#E5E9EA", color: "#646C6F" }}>{t}</span>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
    </div>
  );
}

function TabButton({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 text-sm font-semibold rounded-full transition-colors flex items-center gap-2"
      style={
        active
          ? { background: "#fff", color: "#00664C", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }
          : { color: "#646C6F" }
      }
    >
      {label}
      {count != null && (
        <span
          className="text-xs px-1.5 py-0.5 rounded-full font-bold"
          style={active ? { background: "#B0FCEA", color: "#003326" } : { background: "#E5E9EA", color: "#99A6AB" }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ActionBtn({ onClick, disabled, icon: Icon, label, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-opacity disabled:opacity-50"
      style={style}
    >
      {disabled ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminVagasRemotas() {
  const [tab, setTab] = useState("pending");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const all = await db.entities.JobListing.list("-imported_at");
      setJobs(Array.isArray(all) ? all : []);
    } catch (err) {
      console.error("AdminVagasRemotas load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await db.functions.invoke("sync-vagas-remotas", {});
      if (res.error) {
        // Extrai o corpo real da resposta para mostrar o erro com mais detalhe
        let msg = res.error.message ?? String(res.error);
        if (res.error.context) {
          try {
            const body = await res.error.context.json();
            msg = `HTTP ${res.error.context.status}: ${body.error ?? body.message ?? JSON.stringify(body)}`;
          } catch {
            try { msg = `HTTP ${res.error.context.status}: ${await res.error.context.text()}`; } catch {}
          }
        }
        throw new Error(msg);
      }
      setSyncResult(res.data);
    } catch (err) {
      setSyncResult({ error: String(err.message ?? err) });
    } finally {
      setSyncing(false);
      await loadJobs();
    }
  }

  async function withLoading(id, fn) {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try { await fn(); } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }

  async function approve(job) {
    await withLoading(job.id, async () => {
      await db.entities.JobListing.update(job.id, {
        import_status: "approved",
        is_published: true,
        published_at: new Date().toISOString(),
      });
      await loadJobs();
    });
  }

  async function reject(job) {
    await withLoading(job.id, async () => {
      await db.entities.JobListing.update(job.id, {
        import_status: "rejected",
        is_published: false,
      });
      await loadJobs();
    });
  }

  async function unpublish(job) {
    await withLoading(job.id, async () => {
      await db.entities.JobListing.update(job.id, { is_published: false });
      await loadJobs();
    });
  }

  async function toggleFeatured(job) {
    await withLoading(job.id, async () => {
      await db.entities.JobListing.update(job.id, { is_featured: !job.is_featured });
      await loadJobs();
    });
  }

  async function reapprove(job) {
    await withLoading(job.id, async () => {
      await db.entities.JobListing.update(job.id, {
        import_status: "approved",
        is_published: true,
        published_at: new Date().toISOString(),
      });
      await loadJobs();
    });
  }

  const pending   = jobs.filter(j => j.import_status === "pending");
  const published = jobs.filter(j => j.is_published);
  const rejected  = jobs.filter(j => j.import_status === "rejected");
  const tabJobs   = tab === "pending" ? pending : tab === "published" ? published : rejected;

  const totalImported = syncResult && !syncResult.error
    ? (syncResult.portus ?? 0) + (syncResult.remotive ?? 0) + (syncResult.wwr ?? 0)
      + (syncResult.gupy ?? 0) + (syncResult.vagasremotas ?? 0) + (syncResult.getonboard ?? 0)
      + (syncResult.greenhouse ?? 0) + (syncResult.lever ?? 0) + (syncResult.remoteok ?? 0)
    : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#B0FCEA" }}>
            <Briefcase className="w-5 h-5" style={{ color: "#00664C" }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              Vagas Remotas — Admin
            </h1>
            <p className="text-xs" style={{ color: "#99A6AB" }}>Curadoria de vagas importadas automaticamente</p>
          </div>
        </div>

        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full transition-opacity disabled:opacity-60"
          style={{ background: "#00DCA4", color: "#003326" }}
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncing ? "Sincronizando..." : "Sincronizar agora"}
        </button>
      </div>

      {/* Sync result */}
      {syncResult && (
        <div
          className="rounded-2xl px-5 py-4 flex items-start gap-3 text-sm"
          style={
            syncResult.error || syncResult.errors?.length
              ? { background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }
              : { background: "#EAFAF6", border: "1px solid #00DCA4", color: "#003326" }
          }
        >
          {(syncResult.error || syncResult.errors?.length > 0) && (
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          )}
          <div>
            {syncResult.error ? (
              <p><span className="font-semibold">Erro:</span> {syncResult.error}</p>
            ) : (
              <>
                <p>
                  <span className="font-semibold">{totalImported} nova{totalImported !== 1 ? "s vagas importadas" : " vaga importada"}</span>
                </p>
                <p className="text-xs opacity-80 mt-0.5">
                  Portüs: {syncResult.portus ?? 0} · Gupy: {syncResult.gupy ?? 0} · Greenhouse: {syncResult.greenhouse ?? 0} · Lever: {syncResult.lever ?? 0} · Remotive: {syncResult.remotive ?? 0} · RemoteOK: {syncResult.remoteok ?? 0} · WWR: {syncResult.wwr ?? 0} · VagasRemotas: {syncResult.vagasremotas ?? 0} · GetOnBoard: {syncResult.getonboard ?? 0}
                </p>
                {(syncResult.expired ?? 0) > 0 && (
                  <p className="text-xs opacity-80 mt-0.5">
                    {syncResult.expired} vaga{syncResult.expired !== 1 ? "s despublicadas" : " despublicada"} automaticamente (mais de 15 dias)
                  </p>
                )}
                {syncResult.errors?.length > 0 && (
                  <ul className="mt-1 list-disc list-inside text-xs opacity-80">
                    {syncResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div
        className="flex gap-1 p-1 rounded-2xl w-fit"
        style={{ background: "#E5E9EA" }}
      >
        <TabButton active={tab === "pending"}   onClick={() => setTab("pending")}   label="Pendentes"  count={pending.length} />
        <TabButton active={tab === "published"} onClick={() => setTab("published")} label="Publicadas" count={published.length} />
        <TabButton active={tab === "rejected"}  onClick={() => setTab("rejected")}  label="Rejeitadas" count={rejected.length} />
      </div>

      {/* Job list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-2xl p-4 bg-white animate-pulse" style={{ border: "1px solid #E5E9EA" }}>
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl flex-shrink-0" style={{ background: "#E5E9EA" }} />
                <div className="flex-1 space-y-2">
                  <div className="h-4 rounded-full w-1/2" style={{ background: "#E5E9EA" }} />
                  <div className="h-3 rounded-full w-1/3" style={{ background: "#E5E9EA" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : tabJobs.length === 0 ? (
        <div
          className="rounded-2xl p-12 flex flex-col items-center gap-3 text-center bg-white"
          style={{ border: "1px solid #E5E9EA" }}
        >
          <Briefcase className="w-9 h-9 opacity-20" style={{ color: "#00664C" }} />
          <p className="text-sm" style={{ color: "#99A6AB" }}>
            {tab === "pending"   && "Nenhuma vaga pendente. Clique em \"Sincronizar agora\" para importar."}
            {tab === "published" && "Nenhuma vaga publicada."}
            {tab === "rejected"  && "Nenhuma vaga rejeitada."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tabJobs.map(job => {
            const busy = !!actionLoading[job.id];
            return (
              <JobRow
                key={job.id}
                job={job}
                actions={
                  tab === "pending" ? (
                    <>
                      <ActionBtn
                        onClick={() => approve(job)}
                        disabled={busy}
                        icon={Check}
                        label="Publicar"
                        style={{ background: "#B0FCEA", color: "#003326" }}
                      />
                      <ActionBtn
                        onClick={() => reject(job)}
                        disabled={busy}
                        icon={X}
                        label="Rejeitar"
                        style={{ background: "#FADBDF", color: "#892E38" }}
                      />
                    </>
                  ) : tab === "published" ? (
                    <>
                      <ActionBtn
                        onClick={() => toggleFeatured(job)}
                        disabled={busy}
                        icon={Star}
                        label={job.is_featured ? "Remover destaque" : "Destacar"}
                        style={
                          job.is_featured
                            ? { background: "#00DCA4", color: "#003326" }
                            : { background: "#E5E9EA", color: "#646C6F" }
                        }
                      />
                      <ActionBtn
                        onClick={() => unpublish(job)}
                        disabled={busy}
                        icon={EyeOff}
                        label="Despublicar"
                        style={{ background: "#E5E9EA", color: "#646C6F" }}
                      />
                    </>
                  ) : (
                    <ActionBtn
                      onClick={() => reapprove(job)}
                      disabled={busy}
                      icon={Check}
                      label="Re-aprovar"
                      style={{ background: "#B0FCEA", color: "#003326" }}
                    />
                  )
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
